/**
 * API client for the Observability Dashboard.
 *
 * Wraps fetch calls with:
 * - Bearer token authentication
 * - Base URL configuration
 * - Error handling
 *
 * Implementation plan reference: D1.20 - D1.22
 */

import { getToken } from '@/auth/session';
import { getApiBaseUrl } from '@/api/baseUrl';
import type {
  Environment,
  PipelineResponse,
  ServicesResponse,
  DatabaseResponse,
  AlertsResponse,
  CostsResponse,
  CostsByAppResponse,
  UntaggedResourcesResponse,
  AppCostDetailResponse,
  CoverageResponse,
  AccessibilityResponse,
  AnalyticsResponse,
  AnalyticsFunnelResponse,
  AnalyticsCampaignsResponse,
  AnalyticsMerchantsResponse,
  AnalyticsQualityResponse,
  AnalyticsCohortsResponse,
  AnalyticsOrdersResponse,
  AnalyticsUserActivityResponse,
  CohortGranularity,
  FunnelWindow,
  FunnelSegment,
  SchemaErdResponse,
  DriftResponse,
  DriftSummaryResponse,
  DriftSummaryAggregateResponse,
  ReleaseNotesResponse,
} from '@/types/api';
import type { DriftEnv } from '@/utils/driftConfig';

/**
 * Error thrown when an API request fails.
 *
 * `code` carries the backend's classified error code (CORE-1056c) —
 * e.g. `GITHUB_RATE_LIMITED`, `LLM_ACCESS_DENIED`, `LLM_MODEL_NOT_FOUND`.
 * Optional because non-Blooph-shape responses (network errors, upstream
 * proxies) won't have one. Consumers that want to branch on the failure
 * class should check `err.code`; consumers that just want to render an
 * actionable message should read `err.message` (which is populated from
 * the backend's classified message when available, or falls back to the
 * generic "API request failed" string).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Parse a Blooph error-envelope response body — `{error, message, statusCode, code?}`
 * per src/utils/response.ts in observability-api. Returns `{message, code}` on
 * success or `null` on any deviation (non-JSON, wrong shape, missing message).
 * Kept lenient so a random 500 from an upstream proxy doesn't crash the
 * error-handling path.
 */
function parseBloophErrorBody(body: string | undefined): {
  message: string;
  code?: string;
} | null {
  if (body === undefined || body.length === 0) return null;
  try {
    const parsed: unknown = JSON.parse(body);
    // Reject null (typeof null === 'object'), non-objects (strings /
    // numbers / booleans from a `JSON.parse('"foo"')`), AND arrays —
    // some upstream API-GW default responses aggregate validation
    // errors as `[{message: ...}]`. An array would incidentally pass
    // the null/object guard because typeof [] === 'object', and the
    // downstream `arr.message` lookup is undefined so `msg is not a
    // string` rejects it — but making the intent explicit avoids a
    // future maintainer wondering why the guard reads like it
    // accepts arrays.
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const obj = parsed as { message?: unknown; code?: unknown };
    if (typeof obj.message !== 'string' || obj.message.length === 0) return null;
    return {
      message: obj.message,
      ...(typeof obj.code === 'string' && { code: obj.code }),
    };
  } catch {
    return null;
  }
}

/**
 * Appends required `env` query for env-scoped data routes (see D2.14a).
 */
function withEnvQuery(path: string, env: Environment): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}env=${encodeURIComponent(env)}`;
}

/**
 * Analytics-v2 lane routing (CORE-1117 PR 4).
 *
 * The observability API custom domain has a base-path mapping per stage:
 *   observability-api.blooph.com/dev/…   → dev stage → obs-analytics-v2-dev
 *   observability-api.blooph.com/stage/… → stage stage → obs-analytics-v2-stage
 *   observability-api.blooph.com/prod/…  → prod stage → obs-analytics-v2-prod
 *
 * Analytics-v2 splits into two lanes:
 *   - marketing (funnel, campaigns, merchants, cohorts) — ALWAYS prod because
 *     dev/stage have no meaningful ad traffic or purchase volume; the widgets
 *     exist to answer "how is the real product doing"
 *   - engineering (quality) — follows the caller's env so pipeline-health
 *     issues can be diagnosed per environment
 *
 * The `Environment` type uses 'staging' (historical) while the API gateway
 * base path is 'stage'. Map at the URL boundary — same translation
 * driftConfig.DriftEnv already does for the drift routes.
 */
type AnalyticsV2Stage = 'dev' | 'stage' | 'prod';
const MARKETING_LANE_STAGE: AnalyticsV2Stage = 'prod';

function environmentToStage(env: Environment): AnalyticsV2Stage {
  return env === 'staging' ? 'stage' : env;
}

function analyticsV2Path(stage: AnalyticsV2Stage, subpath: string): string {
  return `/${stage}/analytics-v2/${subpath}`;
}

/**
 * Makes an authenticated GET request to the API.
 */
async function apiGet<T>(path: string): Promise<T> {
  const token = await getToken();
  if (token === null) {
    throw new ApiError('Not authenticated', 401);
  }

  const baseUrl = getApiBaseUrl();

  // Wrap the raw fetch so a network-layer failure (upstream Lambda
  // never responded, API Gateway 504, CORS block, VPN/DNS drop)
  // surfaces as an ApiError with a friendly message instead of the
  // browser-native "Load failed" (Safari) / "Failed to fetch" (Chrome)
  // / "NetworkError" (Firefox) strings. Different browsers stringify
  // the same underlying TypeError differently, all opaque to a
  // non-technical operator. Uses `status: 0` to signal "no HTTP
  // response received" — consumers that check `err.status >= 500`
  // won't false-positive on this.
  //
  // Added under CORE-1056c helper B to complement the backend-side
  // 28s watchdog (helper A): A covers "Lambda ran out of budget and
  // returned a classified 500"; this covers "Lambda never responded
  // at all". Together they turn every "Load failed" into something
  // actionable in the dashboard.
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (err) {
    throw new ApiError(
      'Could not reach the observability API — likely a timeout, network drop, or the upstream service is down. Retry in a moment.',
      0,
      err instanceof Error ? err.message : String(err),
      'NETWORK_ERROR',
    );
  }

  if (!response.ok) {
    const body = await response.text().catch(() => undefined);
    // Prefer the backend's classified error envelope
    // (CORE-1056c) — its `message` is a sanitised, actionable string
    // like "GitHub API rate limit reached — retry in an hour" instead
    // of the opaque "API request failed: 500". Falls back to the
    // generic string when the body isn't a Blooph error envelope
    // (network errors, upstream proxy errors, empty bodies).
    const classified = parseBloophErrorBody(body);
    const message =
      classified?.message ??
      `API request failed: ${response.status} ${response.statusText}`;
    throw new ApiError(message, response.status, body, classified?.code);
  }

  // Wrap the JSON parse so a truncated / malformed 200 body (client
  // abort mid-stream, API GW mid-response timeout, oversized payload
  // cut at Lambda's 6MB limit) doesn't propagate as a raw
  // `SyntaxError: Unexpected end of JSON input` — the same class of
  // opaque browser-native error the fetch-throw wrap above eliminates,
  // just for the response-parse path. Distinct code from NETWORK_ERROR
  // because semantically the server produced SOMETHING; it just wasn't
  // valid JSON. Bounded to `apiGet`'s caller assumption that every
  // 2xx response body is JSON — non-JSON endpoints would need a
  // different helper.
  try {
    return (await response.json()) as T;
  } catch (err) {
    throw new ApiError(
      'The observability API returned a malformed response — likely a truncated or interrupted body. Retry in a moment.',
      response.status,
      err instanceof Error ? err.message : String(err),
      'MALFORMED_RESPONSE',
    );
  }
}

/**
 * Fetches pipeline status for all repositories.
 */
export async function getPipeline(): Promise<PipelineResponse> {
  return apiGet<PipelineResponse>('/pipeline');
}

/**
 * Fetches pipeline status for a specific repository.
 */
export async function getPipelineByRepo(repo: string): Promise<PipelineResponse> {
  return apiGet<PipelineResponse>(`/pipeline/${encodeURIComponent(repo)}`);
}

/**
 * Fetches health status for all ECS services.
 */
export async function getServices(env: Environment): Promise<ServicesResponse> {
  return apiGet<ServicesResponse>(withEnvQuery('/services', env));
}

/**
 * Fetches health status for a specific ECS service.
 */
export async function getServiceById(id: string, env: Environment): Promise<ServicesResponse> {
  return apiGet<ServicesResponse>(
    withEnvQuery(`/services/${encodeURIComponent(id)}`, env),
  );
}

/**
 * Fetches database health and metrics.
 */
export async function getDatabase(env: Environment): Promise<DatabaseResponse> {
  return apiGet<DatabaseResponse>(withEnvQuery('/database', env));
}

/**
 * Fetches active CloudWatch alerts.
 */
export async function getAlerts(): Promise<AlertsResponse> {
  return apiGet<AlertsResponse>('/alerts');
}

/**
 * Fetches AWS cost and usage (Cost Explorer), current vs previous month and forecast.
 */
export async function getCosts(): Promise<CostsResponse> {
  return apiGet<CostsResponse>('/costs');
}

/**
 * Fetches costs grouped by `blooph-cost-app` with shared allocation (Stage 7 Phase 2). No `env` query.
 */
export async function getCostsByApp(): Promise<CostsByAppResponse> {
  return apiGet<CostsByAppResponse>('/costs/by-app');
}

/**
 * Fetches billable rows missing `blooph-cost-app` (Stage 7 Phase 2). No `env` query.
 */
export async function getUntaggedResources(): Promise<UntaggedResourcesResponse> {
  return apiGet<UntaggedResourcesResponse>('/costs/untagged');
}

/**
 * Per-application drill-down (Stage 7 Phase 2). Path segment is encoded for special characters in app keys.
 */
export async function getAppCostDetail(app: string): Promise<AppCostDetailResponse> {
  const encoded = encodeURIComponent(app);
  return apiGet<AppCostDetailResponse>(`/costs/by-app/${encoded}`);
}

/**
 * Fetches code coverage metrics for all microservices (Stage 10).
 */
export async function getCoverage(): Promise<CoverageResponse> {
  return apiGet<CoverageResponse>('/coverage');
}

/**
 * Fetches Google Analytics metrics (active users, traffic, top pages, devices) — Stage 6.
 *
 * Legacy v1 endpoint — the underlying obs-analytics Lambda queries GA4
 * (a single prod property; no env dimension) but sits behind the un-
 * prefixed API-Gateway base-path mapping which routes to the dev stage.
 * That's misleading in access logs — the widget shows the same real prod
 * GA4 data regardless of the dashboard's env dropdown, but URLs say
 * "/analytics" (routed to dev). Prefixing "/prod/" here fixes the URL
 * shape without touching the backend. The audience widget (CORE-1132)
 * will replace this endpoint entirely; this is a bridge until then.
 */
export async function getAnalytics(): Promise<AnalyticsResponse> {
  return apiGet<AnalyticsResponse>('/prod/analytics');
}

/**
 * Fetches the analytics-v2 funnel widget data (ADS-053 / CORE-958).
 *
 * Marketing-lane endpoint per analytics-dashboard-plan.md §6.5: prod-only,
 * no `env` query param (the backend rejects any `?env=` with 400
 * `INVALID_ENV_PARAM`). Source is optional — omit for the combined "all
 * sources" view. Routes through the `/prod/` API-Gateway base path
 * regardless of the dashboard's env dropdown — see analyticsV2Path.
 */
export async function getAnalyticsFunnel(
  input: { window: FunnelWindow; source?: FunnelSegment },
): Promise<AnalyticsFunnelResponse> {
  const params = new URLSearchParams({ window: input.window });
  // Skip the source param for undefined AND for the 'all' sentinel — the
  // backend's SOURCES allowlist rejects 'all' explicitly (parseSource
  // returns INVALID_SOURCE), even though it accepts absent-source as 'all'
  // by default. Guarding here keeps every valid caller shape working.
  if (input.source !== undefined && input.source !== 'all') {
    params.set('source', input.source);
  }
  return apiGet<AnalyticsFunnelResponse>(
    `${analyticsV2Path(MARKETING_LANE_STAGE, 'funnel')}?${params.toString()}`,
  );
}

/**
 * Fetches the analytics-v2 campaigns widget data (ADS-054 / CORE-959).
 *
 * Marketing-lane endpoint per plan §6.5: prod-only, no `env` query param
 * (the backend rejects any `?env=` with 400 `INVALID_ENV_PARAM`). No source
 * segmentation — the campaigns list is a superset across all sources.
 * Routes through the `/prod/` API-Gateway base path — see analyticsV2Path.
 */
export async function getAnalyticsCampaigns(
  input: { window: FunnelWindow },
): Promise<AnalyticsCampaignsResponse> {
  const params = new URLSearchParams({ window: input.window });
  return apiGet<AnalyticsCampaignsResponse>(
    `${analyticsV2Path(MARKETING_LANE_STAGE, 'campaigns')}?${params.toString()}`,
  );
}

/**
 * Fetches the analytics-v2 merchants widget data (ADS-055 / CORE-960).
 *
 * Marketing-lane endpoint per plan §6.5: prod-only, no `env` query param
 * (backend rejects `?env=` with 400 `INVALID_ENV_PARAM`). Returns two
 * ranked lists (landingPages + merchants) in one response, both ranked
 * by conversion rate. Routes through `/prod/` — see analyticsV2Path.
 */
export async function getAnalyticsMerchants(
  input: { window: FunnelWindow },
): Promise<AnalyticsMerchantsResponse> {
  const params = new URLSearchParams({ window: input.window });
  return apiGet<AnalyticsMerchantsResponse>(
    `${analyticsV2Path(MARKETING_LANE_STAGE, 'merchants')}?${params.toString()}`,
  );
}

/**
 * Fetches the data-quality panel data from GET /prod/analytics-v2/quality.
 * Prod-only (marketing lane) — all data sources are production data, so the
 * quality widget always routes through the prod stage like the other
 * analytics-v2 widgets. No `window` param — response has fixed 24h/7d/28d
 * volume columns per surface.
 */
export async function getAnalyticsQuality(): Promise<AnalyticsQualityResponse> {
  return apiGet<AnalyticsQualityResponse>(
    analyticsV2Path(MARKETING_LANE_STAGE, 'quality'),
  );
}

/**
 * Fetches the order volume & GMV trend data (CORE-1201).
 *
 * Engineering-lane endpoint — routes through the env's base path
 * (`/dev/`, `/stage/`, `/prod/`). `window` is required. No `?env=` query
 * param — the API-Gateway stage prefix carries the env signal.
 */
export async function getAnalyticsOrders(
  input: { env: Environment; window: FunnelWindow },
): Promise<AnalyticsOrdersResponse> {
  const params = new URLSearchParams({ window: input.window });
  return apiGet<AnalyticsOrdersResponse>(
    `${analyticsV2Path(environmentToStage(input.env), 'orders')}?${params.toString()}`,
  );
}

/**
 * Fetches the user activity data (CORE-1206).
 *
 * Engineering-lane endpoint — routes through the env's base path
 * (`/dev/`, `/stage/`, `/prod/`). `window` is required. No `?env=` query
 * param — the API-Gateway stage prefix carries the env signal.
 */
export async function getAnalyticsUserActivity(
  input: { env: Environment; window: FunnelWindow },
): Promise<AnalyticsUserActivityResponse> {
  const params = new URLSearchParams({ window: input.window });
  return apiGet<AnalyticsUserActivityResponse>(
    `${analyticsV2Path(environmentToStage(input.env), 'user-activity')}?${params.toString()}`,
  );
}

/**
 * Fetches the acquisition-cohort retention data (ADS-057 / CORE-962).
 *
 * Marketing-lane endpoint per plan §6.5: prod-only, no `env` query param
 * (backend rejects `?env=` with 400 `INVALID_ENV_PARAM`). `granularity`
 * is optional server-side (defaults to `weekly`) but always specified
 * here so the request URL is unambiguous in CloudWatch access logs.
 * Returns 13 months of acquisition-week or acquisition-month cohorts
 * with per-period retention curves. Routes through `/prod/` —
 * see analyticsV2Path.
 */
export async function getAnalyticsCohorts(
  input: { granularity: CohortGranularity },
): Promise<AnalyticsCohortsResponse> {
  const params = new URLSearchParams({ granularity: input.granularity });
  return apiGet<AnalyticsCohortsResponse>(
    `${analyticsV2Path(MARKETING_LANE_STAGE, 'cohorts')}?${params.toString()}`,
  );
}

/**
 * Fetches pa11y accessibility reports for mobile-frontend web builds (S3, keyed by short SHA).
 */
export async function getAccessibility(limit = 30): Promise<AccessibilityResponse> {
  return apiGet<AccessibilityResponse>(`/accessibility?limit=${encodeURIComponent(String(limit))}`);
}

/**
 * Fetches the latest database schema ERD snapshot from S3 (keyed by commit short SHA).
 */
export async function getSchemaErd(): Promise<SchemaErdResponse> {
  return apiGet<SchemaErdResponse>('/schema?limit=1');
}

/**
 * Fetches environment drift between two Blooph environments (CORE-799).
 * Mirrors GET /drift?from=&to= — see drift-response-shape.md.
 */
export async function getDrift(from: DriftEnv, to: DriftEnv): Promise<DriftResponse> {
  return apiGet<DriftResponse>(
    `/drift?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
}

/**
 * Fetches the Claude-generated drift summary for a single service between
 * two environments (CORE-912). Mirrors GET /drift/summary — the underlying
 * Lambda caches responses in S3 with 1h TTL; the `cache_hit` field on the
 * response tells the caller whether this was served from cache.
 *
 * Cold response can take 5-15s (Claude call latency). Callers should show
 * a spinner and consider caching the response in component state for the
 * lifetime of the row.
 */
export async function getDriftSummary(
  from: DriftEnv,
  to: DriftEnv,
  service: string,
): Promise<DriftSummaryResponse> {
  // encodeURIComponent (space → %20) matches sibling getDrift's pattern.
  // URLSearchParams would encode space as `+`, which API Gateway REST does
  // NOT decode back to space in event.queryStringParameters — the `+`-as-
  // space convention only applies to form bodies. All service slugs are
  // hyphenated today so the difference is empirically invisible, but the
  // consistent encoding is one less latent bug for a future service key.
  return apiGet<DriftSummaryResponse>(
    `/drift/summary?from_env=${encodeURIComponent(from)}&to_env=${encodeURIComponent(to)}&service=${encodeURIComponent(service)}`,
  );
}

/**
 * Fetches the rolling-30-day drift-summary spend aggregate (CORE-912
 * PR-A/D endpoint). Consumed by the drift tab's cost strip.
 *
 * Aggregate is global (all services + env-pairs) — no query params.
 * Errors propagate to the caller which is expected to hide the strip
 * gracefully rather than block the drift table (the aggregate is
 * informational; the drift data is the primary content).
 */
export async function getDriftSummaryAggregate(): Promise<DriftSummaryAggregateResponse> {
  return apiGet<DriftSummaryAggregateResponse>('/drift/summary/aggregate');
}

/**
 * Fetches the release-notes envelope for an env-pair promotion (CORE-1011
 * for CORE-1009 → CORE-1010 Lambda). Aggregates per-service drift
 * summaries + cross-service desync signals into one narrative.
 *
 * Response is cached server-side in S3 with 1h TTL; `served_from_cache`
 * on the envelope tells the caller whether this was served from cache.
 * Cold-cache calls (fan-out + reducer, up to 12 per-service Claude calls
 * concurrent-3 + reducer) can take up to ~30s — callers must show a
 * loading affordance and set a matching UI timeout expectation. Note
 * that API Gateway itself will 504 sync invocations past 29s; the Lambda
 * keeps running and populates the cache, so a Try-Again on 504 typically
 * hits cache and returns fast.
 *
 * Uses `from` / `to` query-param names (matches `/drift`; NOT the
 * `from_env` / `to_env` names the per-service `/drift/summary` route uses).
 */
export async function getReleaseNotes(
  from: DriftEnv,
  to: DriftEnv,
): Promise<ReleaseNotesResponse> {
  return apiGet<ReleaseNotesResponse>(
    `/drift/release-notes?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
}
